"use client";

import { MARKETING_HEADER_CHROME_PORTAL_ATTR } from "@marketing/shared/lib/marketing-site-header-outside-interaction";
import { CONTENT_TOOLTIP_CHROME, ContentTooltip } from "@repo/ui";
import { Button } from "@repo/ui/components/button";
import { CircleButton } from "@repo/ui/components/custom/circle-button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@repo/ui/components/dropdown-menu";
import { useShowCursorStyleControls } from "@shared/hooks/use-pointer-fine";
import { useCursorPreference } from "@shared/lib/cursor-preference-context";
import { MousePointer, MousePointerClick } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useIsClient } from "usehooks-ts";

export type CursorStyleToggleAppearance =
	| "ghost"
	| "circle-dark"
	| "circle-glass";

interface CursorStyleToggleProps {
	/**
	 * `ghost` — marketing / default (Button ghost icon).
	 * `circle-glass` — SaaS header glass circle.
	 * `circle-dark` — legacy navy solid.
	 */
	appearance?: CursorStyleToggleAppearance;
}

export function CursorStyleToggle({
	appearance = "ghost",
}: CursorStyleToggleProps) {
	const t = useTranslations("common.cursorStyle");
	const { style, setStyle, hydrated } = useCursorPreference();
	const showCursorControls = useShowCursorStyleControls();
	const isClient = useIsClient();
	const [panelValue, setPanelValue] = useState<string>("browser");

	useEffect(() => {
		setPanelValue(style);
	}, [style]);

	if (!isClient || !showCursorControls || !hydrated) {
		return null;
	}

	const icon =
		style === "custom" ? (
			<MousePointerClick className="size-4" aria-hidden />
		) : (
			<MousePointer className="size-4" aria-hidden />
		);

	return (
		<DropdownMenu modal={false}>
			<ContentTooltip
				{...CONTENT_TOOLTIP_CHROME}
				side="bottom"
				content={t("tooltip")}
			>
				<DropdownMenuTrigger asChild>
					{appearance === "circle-glass" ||
					appearance === "circle-dark" ? (
						<CircleButton
							chrome={
								appearance === "circle-glass"
									? "glass"
									: "solid"
							}
							tone={
								appearance === "circle-glass"
									? "system"
									: "dark"
							}
							size="sm"
							data-test="cursor-style-toggle"
							aria-label={t("ariaLabel")}
						>
							{icon}
						</CircleButton>
					) : (
						<Button
							variant="ghost"
							size="icon"
							data-test="cursor-style-toggle"
							aria-label={t("ariaLabel")}
						>
							{icon}
						</Button>
					)}
				</DropdownMenuTrigger>
			</ContentTooltip>

			<DropdownMenuContent
				className="z-[260]"
				{...{ [MARKETING_HEADER_CHROME_PORTAL_ATTR]: "" }}
			>
				<DropdownMenuRadioGroup
					value={panelValue}
					onValueChange={(next) => {
						if (next !== "browser" && next !== "custom") {
							return;
						}
						setPanelValue(next);
						setStyle(next);
					}}
				>
					<DropdownMenuRadioItem
						value="browser"
						data-test="cursor-style-toggle-browser"
					>
						<MousePointer
							className="mr-2 size-4 opacity-60"
							aria-hidden
						/>{" "}
						{t("browser")}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem
						value="custom"
						data-test="cursor-style-toggle-custom"
					>
						<MousePointerClick
							className="mr-2 size-4 opacity-80"
							aria-hidden
						/>{" "}
						{t("custom")}
					</DropdownMenuRadioItem>
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
